import { FileText, Trash2, UploadCloud } from 'lucide-react';
import { DragEvent, useEffect, useRef, useState } from 'react';
import { fileSize } from '../format';

export const MAX_STATEMENT_MB = 10;

export interface ReadFile {
  file: File;
  contentBase64: string;
}

const isPdf = (file: File) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

/**
 * The statement upload: click or drag in a PDF (at most 10MB, as the backend accepts). The file is read into
 * base64 here, with the progress shown on its card, and can be viewed or removed before it is sent.
 */
const FileDrop = ({ value, onChange, disabled }: { value: ReadFile | null; onChange: (file: ReadFile | null) => void; disabled?: boolean }) => {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(value ? 100 : 0);
  const [reading, setReading] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (!value) {
      setPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(value.file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const read = (file: File) => {
    setError('');
    if (!isPdf(file)) {
      setError('Please upload your statement as a PDF.');
      return;
    }
    if (file.size > MAX_STATEMENT_MB * 1024 * 1024) {
      setError(`The statement must be ${MAX_STATEMENT_MB}MB or smaller.`);
      return;
    }
    setReading(file);
    setProgress(0);
    const reader = new FileReader();
    reader.onprogress = (e) => e.lengthComputable && setProgress(Math.round((e.loaded / e.total) * 100));
    reader.onerror = () => {
      setReading(null);
      setError('The file could not be read. Please try again.');
    };
    reader.onload = () => {
      const result = String(reader.result ?? '');
      setProgress(100);
      setReading(null);
      onChange({ file, contentBase64: result.slice(result.indexOf(',') + 1) });
    };
    reader.readAsDataURL(file);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (!disabled && e.dataTransfer.files?.[0]) {
      read(e.dataTransfer.files[0]);
    }
  };

  const shown = reading ?? value?.file ?? null;

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`rounded-lg border px-4 py-8 text-center transition-colors ${dragging ? 'border-lend-blue bg-lend-blue-soft' : 'border-lend-line bg-white'}`}
      >
        <UploadCloud className="mx-auto h-6 w-6 text-lend-ink" aria-hidden />
        <p className="mt-3 text-sm md:text-base">
          <button
            type="button"
            disabled={disabled}
            onClick={() => input.current?.click()}
            className="text-lend-blue-dark font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-lend-blue rounded"
          >
            Click to upload
          </button>{' '}
          <span className="text-lend-body">or drag and drop</span>
        </p>
        <p className="mt-1 text-xs md:text-sm text-lend-muted">PDF (max. {MAX_STATEMENT_MB}MB)</p>
        <input
          ref={input}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              read(file);
            }
            e.target.value = '';
          }}
        />
      </div>

      {error && <p className="text-sm text-error-600">{error}</p>}

      {shown && (
        <div className="rounded-lg border border-lend-line p-4">
          <div className="flex items-start gap-3">
            <FileText className="h-6 w-6 text-lend-blue shrink-0" aria-hidden />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{shown.name}</p>
              <p className="text-xs text-lend-muted">{fileSize(shown.size)}</p>
            </div>
            <button
              type="button"
              aria-label={`Remove ${shown.name}`}
              disabled={disabled || !!reading}
              onClick={() => onChange(null)}
              className="text-lend-muted hover:text-error-600"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-lend-body">
            <span>{reading ? 'Uploading' : 'Completed'}</span>
            <span>{reading ? progress : 100}%</span>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-gray-200 overflow-hidden">
            <div className="h-full bg-lend-blue transition-all" style={{ width: `${reading ? progress : 100}%` }} />
          </div>
          {value && previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 block w-full rounded bg-gray-200 py-2 text-center text-sm text-lend-ink hover:bg-gray-300"
            >
              View file
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default FileDrop;
